import PiezaPlanoMaterial from "../../components/PiezaPlanoMaterial";
import React, { useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Select from "react-select";
import { useNavigate, useNavigationType } from "react-router-dom";

import backend from "../../backend";
import WaitAsync from "../../components/WaitAsync";
import useTitle from "../../hooks/useTitle";
import IntegerInput from "../../components/IntegerInput";
import { formatISODate } from "../../utils";

const selectStyles = {
  option: styles => ({ ...styles, color: "black" }),
  control: styles => ({ ...styles, flexDirection: "row" }),
  input: styles => ({ ...styles, flexDirection: "row" }),
};

const getNewItem = () => ({
  piece: null,
  quantity: 1,
  stockAvailable: 0,
});

const CustomerOrderForm = () => {
  useTitle("Registrar OC");
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  const waitRef = useRef();

  const [clients, setClients] = useState([]);
  const [pieces, setPieces] = useState([]);

  const [client, setClient] = useState(null);
  const [clientOCNumber, setClientOCNumber] = useState("");
  const [date, setDate] = useState(formatISODate(new Date()));
  const [items, setItems] = useState([getNewItem()]);
  const [observations, setObservations] = useState("");

  const [validated, setValidated] = useState(false);
  const [error, setError] = useState();

  // D-Stock-Cruce — modal de confirmación con cruce de inventario antes del POST
  const [cruceModal, setCruceModal] = useState(null); // { decisions: { [idx]: useFromStock } }
  const [postCreateAlert, setPostCreateAlert] = useState(null); // { belowReorder: [...], newCO }

  const getData = async () => {
    try {
      const [piecesRes, entitiesRes] = await Promise.all([
        backend.get("piece"),
        backend.get("entity"),
      ]);
      setPieces(
        piecesRes.data.map(v => ({
          ...v,
          value: v._id,
          label: `${v.sku ? v.sku + " - " : ""}${v.name}`,
        }))
      );
      setClients(
        entitiesRes.data
          .filter(v => v.type === "client")
          .map(v => ({
            ...v,
            value: v._id,
            label: `${v.rut ? v.rut + " - " : ""}${v.name}`,
          }))
      );
    } catch (err) {
      console.log(err);
    }
  };

  const cancel = () =>
    navigationType === "POP"
      ? navigate("/customerOrders", { replace: true })
      : navigate(-1);

  // Validación inicial → si hay piezas con stock disponible, abre modal de cruce.
  // Si no, va directo al POST.
  const handleRegisterClick = () => {
    setValidated(false);
    setError();
    try {
      if (!client) throw new Error("Selecciona un cliente");
      if (!date) throw new Error("Ingresa una fecha");
      if (items.length === 0) throw new Error("Agrega al menos un producto");
      for (const item of items) {
        if (!item.piece) throw new Error("Selecciona una pieza en cada fila");
        if (!item.quantity || item.quantity <= 0) throw new Error("Cantidad inválida");
      }
    } catch (err) {
      setError(err.message); setValidated(true); return;
    }

    // ¿Alguna pieza tiene stock disponible? Si sí, abrir modal de cruce.
    const hasAnyStock = items.some(it => (it.piece?.stock || 0) > 0);
    if (!hasAnyStock) {
      // Sin stock en ningún ítem → submit directo, todos a fabricar.
      doSubmit(items.map(() => 0));
      return;
    }

    // Pre-cargar decisión: usar todo el stock disponible (capeado a la qty pedida).
    const decisions = {};
    items.forEach((it, i) => {
      const stock = it.piece?.stock || 0;
      decisions[i] = Math.min(stock, Number(it.quantity) || 0);
    });
    setCruceModal({ decisions });
  };

  const doSubmit = async (consumeArr) => {
    setValidated(false);
    setError();
    setCruceModal(null);
    try {
      waitRef.current?.setLoading(true);

      const data = {
        client: client.value,
        clientOCNumber: clientOCNumber || undefined,
        date,
        observations: observations || undefined,
        items: items.map((item, i) => ({
          piece: item.piece.value,
          sku: item.piece.sku,
          quantity: Number(item.quantity),
          stockAvailable: Number(consumeArr[i] || 0),
          toManufacture: Math.max(0, Number(item.quantity) - Number(consumeArr[i] || 0)),
        })),
      };

      const newCO = (await backend.post("customerOrder", data)).data;
      // ¿Algo quedó bajo el mínimo? Si sí, mostrar aviso antes de navegar.
      const belowReorder = (newCO.stockConsumed || []).filter(s => s.belowReorder);
      if (belowReorder.length > 0) {
        setPostCreateAlert({ belowReorder, newCO });
      } else {
        navigate("/customerOrders", { replace: true, state: { new: newCO } });
      }
    } catch (err) {
      console.log(err);
      setError(err?.response?.data?.error || err?.message);
      setValidated(true);
    }
    waitRef.current?.setLoading(false);
  };

  return (
    <WaitAsync func={getData} ref={waitRef}>
      <div className="fw-bold fs-1 mb-3">Nueva OC de Cliente</div>
      <Form
        noValidate
        validated={validated}
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className="m3-col">
          <Select
            styles={selectStyles}
            isClearable
            onChange={setClient}
            value={client}
            options={clients}
            placeholder="Cliente *"
            classNamePrefix="react-select"
          />

          <Form.Group>
            <Form.Control
              onChange={e => setClientOCNumber(e.target.value)}
              value={clientOCNumber}
              placeholder="N° OC del cliente"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="text-muted mb-1">Fecha de OC</Form.Label>
            <Form.Control
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              required
            />
          </Form.Group>

          <div className="fw-bold fs-5 mt-2">Productos</div>
          {/* móvil: la grilla de columnas fijas scrollea horizontal dentro del contenedor */}
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", maxWidth: "100%" }}>
            <div
              style={{
                display: "grid",
                gap: "12px 8px",
                gridTemplateColumns: "minmax(200px,1fr) 100px 130px auto",
                minWidth: 480,
              }}
            >
              <div className="fw-semibold text-muted">Pieza</div>
              <div className="fw-semibold text-muted text-center">Cantidad</div>
              <div className="fw-semibold text-muted text-center">Stock catálogo</div>
              <div />
              {items.map((item, i) => (
                <ItemRow key={i} index={i} item={item} pieces={pieces} setItems={setItems} />
              ))}
            </div>
          </div>
          <div style={{ fontSize: 11, color: "#aaa", marginTop: 4, fontStyle: "italic" }}>
            ℹ Si una pieza tiene stock, al registrar te preguntaremos cuánto usar de inventario vs fabricar.
          </div>

          <Button
            variant="outline-primary"
            onClick={() => setItems(p => [...p, getNewItem()])}
          >
            <i className="bi bi-plus-lg me-2" />
            Agregar producto
          </Button>

          <Form.Group>
            <Form.Control
              as="textarea"
              rows={2}
              onChange={e => setObservations(e.target.value)}
              value={observations}
              placeholder="Observaciones (opcional)"
            />
          </Form.Group>

          {error && (
            <div className="invalid-feedback text-center d-block">{error}</div>
          )}

          <div className="flex-row align-self-center mt-2">
            <Button variant="secondary" onClick={cancel}>
              Cancelar
            </Button>
            <Button variant="primary ms-3" onClick={handleRegisterClick}>
              Registrar OC
            </Button>
          </div>
        </div>
      </Form>

      {cruceModal && (
        <CruceInventarioModal
          items={items}
          decisions={cruceModal.decisions}
          onCancel={() => setCruceModal(null)}
          onConfirm={decisions => doSubmit(items.map((_, i) => decisions[i] || 0))}
        />
      )}

      {postCreateAlert && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050,
        }}>
          <div style={{
            background: "#1a1a1a", color: "#ddd", borderRadius: 8,
            padding: "20px 24px", width: "min(560px, 92vw)",
            border: "1px solid #4a3500", fontFamily: "Arial, sans-serif", fontSize: 13,
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#f39c12", marginBottom: 6 }}>
              ⚠ Stock bajo el mínimo
            </div>
            <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 14 }}>
              La OC se registró correctamente. Estas piezas quedaron bajo el punto de reposición y necesitan reponerse:
            </div>
            <div style={{ background: "#0d0d0d", border: "1px solid #2e2e2e", borderRadius: 6, padding: "8px 12px", marginBottom: 14 }}>
              {postCreateAlert.belowReorder.map(s => (
                <div key={s.pieceId} style={{ fontSize: 12, padding: "3px 0" }}>
                  <span style={{ color: "#3498db", fontFamily: "monospace", marginRight: 6 }}>{s.sku}</span>
                  <span style={{ color: "#fff" }}>{s.name}</span>
                  <span style={{ color: "#cbd5e1", marginLeft: 6 }}>· stock ahora: <strong style={{ color: "#e74c3c" }}>{s.newStock}</strong></span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => navigate("/customerOrders", { replace: true, state: { new: postCreateAlert.newCO } })}
                style={{ background: "transparent", color: "#cbd5e1", border: "1px solid #444", borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer" }}>
                Continuar
              </button>
              <button onClick={() => navigate("/inventory", { replace: true })}
                style={{ background: "#f39c12", color: "#1a1500", border: "none", borderRadius: 6, padding: "7px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Ir a /inventario
              </button>
            </div>
          </div>
        </div>
      )}
    </WaitAsync>
  );
};

// ─── Modal cruce con inventario ──────────────────────────────────────────────
const CruceInventarioModal = ({ items, decisions: initial, onCancel, onConfirm }) => {
  const [decisions, setDecisions] = useState(initial);

  const set = (idx, val) => setDecisions(d => ({ ...d, [idx]: Math.max(0, Math.min(val, items[idx].piece?.stock || 0, Number(items[idx].quantity) || 0)) }));

  const totals = items.reduce((acc, it, i) => {
    const consume = decisions[i] || 0;
    const fab     = Math.max(0, (Number(it.quantity) || 0) - consume);
    acc.consume += consume;
    acc.fabricate += fab;
    return acc;
  }, { consume: 0, fabricate: 0 });

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050,
    }}>
      <div style={{
        background: "#1a1a1a", color: "#ddd", borderRadius: 8,
        padding: "20px 24px", width: "min(720px, 92vw)",
        maxHeight: "90vh", overflow: "auto",
        border: "1px solid #2e2e2e", fontFamily: "Arial, sans-serif", fontSize: 13,
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 6 }}>
          Cruce con inventario
        </div>
        <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 16 }}>
          Algunas piezas tienen stock disponible. Decidí cuánto usar de stock vs fabricar.
        </div>

        <div style={{ borderTop: "1px solid #2e2e2e" }}>
          {items.map((it, i) => {
            const piece    = it.piece;
            const stock    = piece?.stock || 0;
            const qty      = Number(it.quantity) || 0;
            const consume  = decisions[i] || 0;
            const fab      = Math.max(0, qty - consume);
            const hasStock = stock > 0;

            return (
              <div key={i} style={{
                padding: "12px 0", borderBottom: "1px solid #2e2e2e",
                display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center",
              }}>
                <div>
                  <div style={{ color: "#fff", fontWeight: 600, marginBottom: 2 }}>
                    {piece?.sku && <span style={{ color: "#3498db", fontFamily: "monospace", marginRight: 6 }}>{piece.sku}</span>}
                    {piece?.label || piece?.name}
                  </div>
                  <div style={{ fontSize: 11, color: "#cbd5e1" }}>
                    Pedido: <strong style={{ color: "#ddd" }}>{qty}</strong>
                    {" · "}
                    Stock disponible: {hasStock
                      ? <strong style={{ color: "#2ecc71" }}>{stock}</strong>
                      : <span style={{ color: "#aaa" }}>0 (sin stock)</span>}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                  {hasStock ? (
                    <>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ fontSize: 10, color: "#cbd5e1" }}>Usar de stock:</span>
                        <input type="number" min={0} max={Math.min(stock, qty)}
                          value={consume}
                          onChange={e => set(i, Number(e.target.value) || 0)}
                          style={{
                            width: 60, padding: "4px 6px", textAlign: "center",
                            background: "#161616", border: "1px solid #333",
                            borderRadius: 4, color: "#ddd", fontSize: 12,
                          }} />
                        <button type="button"
                          onClick={() => set(i, Math.min(stock, qty))}
                          style={{
                            fontSize: 10, padding: "3px 8px",
                            background: "#0d2a15", color: "#2ecc71",
                            border: "1px solid #0d4020", borderRadius: 4, cursor: "pointer",
                          }}>Todo</button>
                        <button type="button"
                          onClick={() => set(i, 0)}
                          style={{
                            fontSize: 10, padding: "3px 8px",
                            background: "#2d1a00", color: "#f39c12",
                            border: "1px solid #4a3500", borderRadius: 4, cursor: "pointer",
                          }}>Nada</button>
                      </div>
                      <div style={{ fontSize: 10, color: "#cbd5e1" }}>
                        → {consume} de stock + <strong style={{ color: fab > 0 ? "#f39c12" : "#666" }}>{fab} a fabricar</strong>
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 11, color: "#f39c12" }}>
                      → {qty} a fabricar
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          marginTop: 12, padding: "10px 12px",
          background: "#0d0d0d", borderRadius: 6, border: "1px solid #2e2e2e",
          fontSize: 12,
        }}>
          <strong style={{ color: "#fff" }}>Resumen:</strong>{" "}
          <span style={{ color: "#2ecc71" }}>{totals.consume}</span> uds. de stock
          {" · "}
          <span style={{ color: "#f39c12" }}>{totals.fabricate}</span> uds. a fabricar
          {totals.fabricate === 0 && (
            <div style={{ marginTop: 4, fontSize: 11, color: "#cbd5e1" }}>
              ℹ Toda la OC se cumple con stock — no se generará OT de taller.
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onCancel} style={{
            background: "transparent", color: "#cbd5e1",
            border: "1px solid #444", borderRadius: 6,
            padding: "7px 14px", fontSize: 12, cursor: "pointer",
          }}>Volver al formulario</button>
          <button onClick={() => onConfirm(decisions)} style={{
            background: "#3498db", color: "#fff",
            border: "none", borderRadius: 6,
            padding: "7px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer",
          }}>Confirmar y registrar OC</button>
        </div>
      </div>
    </div>
  );
};

const ItemRow = ({ index, item, pieces, setItems }) => {
  const { piece, quantity } = item;
  const stockCatalogo = piece?.stock || 0;

  const set = key => value =>
    setItems(p => {
      const n = [...p];
      n[index] = { ...n[index], [key]: value };
      return n;
    });

  const remove = () =>
    setItems(p => {
      const n = [...p];
      n.splice(index, 1);
      return n;
    });

  const handlePieceChange = value => {
    setItems(p => {
      const n = [...p];
      n[index] = { ...n[index], piece: value };
      return n;
    });
  };

  return (
    <>
      <Select
        styles={{
          option: styles => ({ ...styles, color: "black" }),
          control: styles => ({ ...styles, flexDirection: "row" }),
          input: styles => ({ ...styles, flexDirection: "row" }),
        }}
        isClearable
        onChange={handlePieceChange}
        value={piece}
        options={pieces}
        placeholder="Seleccionar pieza"
        classNamePrefix="react-select"
      />
      <Form.Group>
        <IntegerInput
          value={quantity}
          onChange={e => set("quantity")(e.target.value || 1)}
          style={{ textAlign: "right" }}
          required
        />
      </Form.Group>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12,
        color: stockCatalogo > 0 ? "#2ecc71" : "#666",
        fontWeight: stockCatalogo > 0 ? 600 : 400,
      }}>
        {piece ? (stockCatalogo > 0 ? `${stockCatalogo} en stock` : "sin stock") : "—"}
      </div>
      <Button variant="secondary justify-content-center" onClick={remove}>
        <i className="bi bi-x-lg" />
      </Button>
      {/* 15-sep-2026 (pedido Maxi): la pieza de una OC debería tener plano y material en
          bruto definidos. Si falta alguno, se completa aquí mismo, sin ir al catálogo. */}
      {piece?._id && (
        <div style={{ gridColumn: "1 / -1", marginTop: -6, paddingLeft: 2 }}>
          <PiezaPlanoMaterial pieza={piece} compacto
            onChange={cambios => handlePieceChange({ ...piece, ...cambios })} />
        </div>
      )}
    </>
  );
};

export default CustomerOrderForm;
